import {useEffect, useState, useRef, MutableRefObject} from 'react';

export type UseIntersectionReturnType = [
    ref: MutableRefObject<HTMLElement>,
    intersectionObserverEntry: Partial<IntersectionObserverEntry>,
];

/**
 * See ./pages/new-terms/NewTerms.tsx for example usecase
 * reference: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#a_simple_example
 */
const useIntersection = (options?: IntersectionObserverInit): UseIntersectionReturnType => {
    const ref = useRef<HTMLElement>();
    const [entry, setEntry] = useState<Partial<IntersectionObserverEntry>>({});

    useEffect(() => {
        if (!ref.current) return;

        // polyfill added, but handle rare case
        if (typeof IntersectionObserver !== 'function') {
            setEntry({isIntersecting: true});
            return;
        }

        const handler = (entries: IntersectionObserverEntry[]) => {
            setEntry(entries[0]);
        };

        const observer = new IntersectionObserver(handler, {
            root: null,
            ...options
        });

        observer.observe(ref.current);
        return () => {
            setEntry({});
            observer.disconnect();
        };
    }, [ref.current]);

    return [ref, entry];
};

export default useIntersection;
